import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Search, WandSparkles } from "lucide-react";
import { Button } from "components/button";
import { Text } from "components/text";
import { useAnalytics } from "contexts/AnalyticsContext";
import type { SeasonalEventSurface } from "contexts/AnalyticsContext/AnalyticsEvents";
import { cn } from "utils";
import type { SeasonalEventPrompt } from "integrations/firebase/collections/seasonalEvents";

const VISIBLE_PROMPT_COUNT = 3;
const REFRESH_DELAY_MS = 400;

const promptsAt = (prompts: SeasonalEventPrompt[], page: number) =>
  prompts.slice(
    page * VISIBLE_PROMPT_COUNT,
    page * VISIBLE_PROMPT_COUNT + VISIBLE_PROMPT_COUNT,
  );

interface SuggestedSearchesProps {
  eventId: string;
  prompts: SeasonalEventPrompt[];
  surface: SeasonalEventSurface;
  /** False while another modal view (e.g. the calendar) covers the prompts. */
  isVisible: boolean;
  onRunSearch: (
    prompt: SeasonalEventPrompt,
    refreshIndex: number,
    eventId: string,
  ) => void;
}

export const SuggestedSearches = ({
  eventId,
  prompts,
  surface,
  isVisible,
  onRunSearch,
}: SuggestedSearchesProps): ReactElement => {
  const { t } = useTranslation(["components"]);
  const { gaEvent } = useAnalytics();
  const [page, setPage] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExhausted, setIsExhausted] = useState(false);
  const refreshTimeoutRef = useRef<number | undefined>(undefined);
  const isVisibleRef = useRef(isVisible);

  const pageCount = Math.max(
    1,
    Math.ceil(prompts.length / VISIBLE_PROMPT_COUNT),
  );
  const visiblePrompts = promptsAt(prompts, page);
  const promptIds = visiblePrompts.map((prompt) => prompt.prompt_id).join(",");

  useEffect(() => {
    gaEvent({
      type: "seasonal_prompts_shown",
      payload: {
        event_id: eventId,
        prompt_count: visiblePrompts.length,
        prompt_ids: promptIds,
        surface,
      },
    });
  }, [gaEvent, eventId, surface, promptIds, visiblePrompts.length]);

  useEffect(() => () => window.clearTimeout(refreshTimeoutRef.current), []);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  let statusMessage = "";
  let isStatusVisible = false;
  if (isRefreshing) {
    statusMessage = t("components:seasonalEventModal.refreshing");
    isStatusVisible = true;
  } else if (isExhausted) {
    statusMessage = t("components:seasonalEventModal.noNewPrompts");
    isStatusVisible = true;
  } else if (refreshCount > 0) {
    statusMessage = t("components:seasonalEventModal.refreshed");
  }

  const handleRefresh = (): void => {
    if (isRefreshing) {
      return;
    }
    setIsRefreshing(true);
    refreshTimeoutRef.current = window.setTimeout(() => {
      // Abandon the swap if the prompts got covered (e.g. calendar opened),
      // so the new set and its impression analytics never land off-screen.
      if (!isVisibleRef.current) {
        setIsRefreshing(false);
        return;
      }
      const nextPage = (page + 1) % pageCount;
      const exhausted = isExhausted || nextPage <= page;
      const refreshIndex = refreshCount + 1;
      setPage(nextPage);
      setRefreshCount(refreshIndex);
      setIsExhausted(exhausted);
      setIsRefreshing(false);
      gaEvent({
        type: "prompts_refreshed",
        payload: {
          surface,
          event_id: eventId,
          refresh_index: refreshIndex,
          new_prompt_ids: promptsAt(prompts, nextPage)
            .map((prompt) => prompt.prompt_id)
            .join(","),
          exhausted,
        },
      });
    }, REFRESH_DELAY_MS);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <WandSparkles className="size-4 text-primary" aria-hidden />
          <Text as="h3" variant="overline">
            {t("components:seasonalEventModal.suggestedSearches")}
          </Text>
        </div>
        <Button
          type="button"
          variant="tertiary"
          size="small"
          className="size-8 justify-center rounded-full p-0"
          aria-label={t("components:seasonalEventModal.refreshAriaLabel")}
          icon="refresh-cw"
          iconClassName={isRefreshing ? "animate-spin" : undefined}
          aria-disabled={isRefreshing}
          onClick={handleRefresh}
        />
      </div>

      <Text
        as="p"
        variant="muted"
        role="status"
        className={cn(!isStatusVisible && "sr-only")}
      >
        {statusMessage}
      </Text>

      <ul
        aria-busy={isRefreshing}
        className={cn(
          "flex flex-col gap-2",
          isRefreshing && "pointer-events-none opacity-50",
        )}
      >
        {visiblePrompts.map((prompt) => (
          <li key={prompt.prompt_id}>
            <button
              type="button"
              disabled={isRefreshing}
              onClick={() => onRunSearch(prompt, refreshCount, eventId)}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl border border-border bg-background px-3 py-3 text-left",
                "transition-colors hover:bg-accent hover:text-accent-foreground",
              )}
            >
              <Search
                className="size-4 shrink-0 text-secondary-400"
                aria-hidden
              />
              <Text as="span" variant="body" className="min-w-0 flex-1">
                {prompt.text}
              </Text>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
